'use client';

import { useCallback, useEffect, useState } from 'react';

import { clearRateLimit, getRateLimit, setRateLimit } from '../_utils/rateLimitStorage';

interface UseRateLimitReturn {
    isRateLimited: boolean;
    resetsAt: Date | null;
    activateRateLimit: (expiresAt: number) => void;
}

export function useRateLimit(): UseRateLimitReturn {
    const [expiresAt, setExpiresAt] = useState<number | null>(() => {
        const entry = getRateLimit();
        if (!entry) return null;
        if (entry.expiresAt <= Date.now()) {
            clearRateLimit();
            return null;
        }
        return entry.expiresAt;
    });

    useEffect(() => {
        if (expiresAt === null) return;

        const remaining = Math.max(0, expiresAt - Date.now());

        const timer = setTimeout(() => {
            clearRateLimit();
            setExpiresAt(null);
        }, remaining);

        return (): void => clearTimeout(timer);
    }, [expiresAt]);

    const activateRateLimit = useCallback((newExpiresAt: number): void => {
        const current = getRateLimit();
        if (current && current.expiresAt > Date.now()) return;
        setRateLimit(newExpiresAt);
        setExpiresAt(newExpiresAt);
    }, []);

    return {
        isRateLimited: expiresAt !== null,
        resetsAt: expiresAt !== null ? new Date(expiresAt) : null,
        activateRateLimit,
    };
}
